import { useCallback, useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Loader2, Save } from "lucide-react";
import WorldMap, { countryName } from "./WorldMap";
import { SectionHeading } from "./ui";

const RANGES = [["7d", "7 days"], ["30d", "30 days"], ["90d", "90 days"], ["all", "All time"]];
const METRICS = [
  ["total", "Total revenue"], ["viewers", "Viewers"],
  ["rentals", "Rentals"], ["watch_party", "Watch Parties"], ["donations", "Donations"],
];
const naira = (n) => `₦${Math.round(n || 0).toLocaleString("en-NG")}`;
const metricValue = (c, m) => (m === "viewers" ? c.viewers : c.revenue[m]);
const fmt = (v, m) => (m === "viewers" ? (v || 0).toLocaleString() : naira(v));

const Pills = ({ items, value, onChange, testPrefix }) => (
  <div className="flex flex-wrap gap-1.5">
    {items.map(([k, label]) => (
      <button
        key={k}
        type="button"
        onClick={() => onChange(k)}
        className={`px-3 py-1.5 rounded-full text-xs font-semibold border transition-colors ${value === k ? "bg-terracotta text-white border-terracotta" : "border-white/10 text-foreground/65 hover:text-foreground hover:border-white/25"}`}
        data-testid={`${testPrefix}-${k}`}
      >
        {label}
      </button>
    ))}
  </div>
);

const CountryCard = ({ c, metric }) => (
  <div className="rounded-xl border border-white/10 bg-black/60 backdrop-blur px-4 py-3 text-xs min-w-[200px]" data-testid="geo-country-card">
    <p className="font-display text-base font-bold mb-1.5">{countryName(c.code)}</p>
    {[["viewers", "Viewers"], ["total", "Total revenue"], ["rentals", "Rentals"], ["watch_party", "Watch Parties"], ["donations", "Donations"]].map(([k, l]) => (
      <p key={k} className={`flex justify-between gap-6 ${k === metric ? "text-gold" : "text-foreground/70"}`}>
        <span>{l}</span><span className="tabular-nums">{fmt(metricValue(c, k), k)}</span>
      </p>
    ))}
  </div>
);

const RateEditor = ({ rate, onSaved }) => {
  const [val, setVal] = useState(rate);
  const [busy, setBusy] = useState(false);
  useEffect(() => setVal(rate), [rate]);
  const save = async () => {
    setBusy(true);
    try {
      await api.put("/admin/geo-insights/rate", { usd_ngn: Number(val) });
      toast.success("Exchange rate saved");
      onSaved();
    } catch {
      toast.error("Enter a rate above 1");
    } finally {
      setBusy(false);
    }
  };
  return (
    <div className="flex items-center gap-2 text-xs text-foreground/60">
      <span>$1 =</span>
      <span className="relative">
        <span className="absolute left-2.5 top-1/2 -translate-y-1/2 text-foreground/50">₦</span>
        <Input type="number" min={1} value={val} onChange={(e) => setVal(e.target.value)} className="w-28 h-8 pl-6 bg-white/5 border-white/10 text-foreground text-base sm:text-sm" data-testid="geo-rate-input" />
      </span>
      <Button size="sm" onClick={save} disabled={busy} className="h-8 bg-gold text-ink hover:bg-gold/90" data-testid="geo-rate-save">
        {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Save className="w-3.5 h-3.5" />}
      </Button>
    </div>
  );
};

export default function GeoInsights() {
  const [range, setRange] = useState("30d");
  const [metric, setMetric] = useState("total");
  const [data, setData] = useState(null);
  const [hover, setHover] = useState(null);
  const [selected, setSelected] = useState(null);

  const load = useCallback(() => {
    api.get("/admin/geo-insights", { params: { range } }).then(({ data: d }) => setData(d))
      .catch(() => toast.error("Couldn't load Geo Insights"));
  }, [range]);
  useEffect(() => { load(); }, [load]);

  const byCode = useMemo(() => Object.fromEntries((data?.countries || []).map((c) => [c.code, c])), [data]);
  const values = useMemo(() => Object.fromEntries((data?.countries || []).map((c) => [c.code, metricValue(c, metric)])), [data, metric]);
  const ranked = useMemo(() => (data?.countries || []).filter((c) => metricValue(c, metric) > 0)
    .sort((a, b) => metricValue(b, metric) - metricValue(a, metric)), [data, metric]);
  const focus = byCode[hover] || byCode[selected];
  const total = data ? (metric === "viewers" ? data.totals.viewers : data.totals[metric]) : 0;

  return (
    <section data-testid="geo-insights">
      <SectionHeading
        eyebrow="Geo Insights"
        title="Where viewers and revenue come from"
        description="Revenue is shown in Naira; USD payments are converted at your rate. Countries come from where each payment or sign-up was made."
        action={data && <RateEditor rate={data.usd_ngn_rate} onSaved={load} />}
      />
      <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
        <Pills items={METRICS} value={metric} onChange={setMetric} testPrefix="geo-metric" />
        <Pills items={RANGES} value={range} onChange={setRange} testPrefix="geo-range" />
      </div>
      {!data ? (
        <div className="flex justify-center py-24"><Loader2 className="w-7 h-7 text-terracotta animate-spin" /></div>
      ) : (
        <div className="grid grid-cols-1 xl:grid-cols-[minmax(0,1fr)_320px] gap-5">
          <div className="relative rounded-2xl border border-white/10 bg-surface p-3 sm:p-5 overflow-hidden">
            <WorldMap values={values} selected={selected} onHover={setHover} onSelect={setSelected} />
            <div className="absolute bottom-3 left-3 sm:bottom-5 sm:left-5 pointer-events-none">
              {focus ? <CountryCard c={focus} metric={metric} /> : (
                <p className="text-[11px] text-foreground/50 bg-black/50 rounded-full px-3 py-1" data-testid="geo-map-hint">Hover or tap a country</p>
              )}
            </div>
          </div>
          <div className="rounded-2xl border border-white/10 bg-surface p-5" data-testid="geo-country-table">
            <p className="text-[11px] uppercase tracking-[0.2em] text-foreground/50">{METRICS.find(([k]) => k === metric)[1]} · total</p>
            <p className="font-display text-3xl font-black text-gold mt-1 mb-4" data-testid="geo-total">{fmt(total, metric)}</p>
            {ranked.length === 0 && <p className="text-sm text-foreground/50" data-testid="geo-empty">No data for this period yet.</p>}
            <ol className="space-y-1 max-h-[340px] overflow-y-auto pr-1">
              {ranked.map((c, i) => {
                const v = metricValue(c, metric);
                return (
                  <li key={c.code}>
                    <button
                      type="button"
                      onClick={() => setSelected(c.code === selected ? null : c.code)}
                      className={`w-full text-left rounded-lg px-2.5 py-2 transition-colors ${selected === c.code ? "bg-terracotta/15" : "hover:bg-white/[0.04]"}`}
                      data-testid={`geo-row-${c.code}`}
                    >
                      <div className="flex justify-between text-sm">
                        <span><span className="text-foreground/40 tabular-nums mr-2">{i + 1}</span>{countryName(c.code)}</span>
                        <span className="tabular-nums font-semibold">{fmt(v, metric)}</span>
                      </div>
                      <div className="h-1 rounded-full bg-white/5 mt-1.5 overflow-hidden">
                        <div className="h-full bg-terracotta rounded-full" style={{ width: `${total ? (v / total) * 100 : 0}%` }} />
                      </div>
                    </button>
                  </li>
                );
              })}
            </ol>
          </div>
        </div>
      )}
    </section>
  );
}
